/**
 * Document Uploader Component
 *
 * Main upload component with file picker integration, drag-and-drop support (web),
 * progress indicators, and error handling.
 */

import { useState, useCallback } from 'react';
import { View, Text, Platform } from 'react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { Upload, FileText, Image } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  useFilePicker,
  useFileUpload,
  type StorageBucket,
  type FilePickResult,
  type UploadResult,
} from '@/lib/storage';
import { UploadProgress } from './UploadProgress';
import AnimatedPressable from '@/components/AnimatedPressable';

interface DocumentUploaderProps {
  bucket?: StorageBucket;
  path?: string;
  onUploadComplete?: (result: UploadResult) => void;
  onUploadError?: (error: string) => void;
  allowImages?: boolean;
  allowMultiple?: boolean;
  title?: string;
  subtitle?: string;
}

export function DocumentUploader({
  bucket = 'dataroom',
  path,
  onUploadComplete,
  onUploadError,
  allowImages = false,
  allowMultiple = false,
  title = 'Upload Document',
  subtitle = 'PDF, Word, Excel, or PowerPoint files',
}: DocumentUploaderProps) {
  const colors = useThemeColors();
  const [selectedFile, setSelectedFile] = useState<FilePickResult | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');

  const { pickDocument, pickImage, isLoading: isPicking, error: pickError } = useFilePicker({ bucket });
  const { upload, isUploading, progress, error: uploadError } = useFileUpload({
    bucket,
    path,
    onSuccess: (result) => {
      setUploadStatus('success');
      onUploadComplete?.(result);
    },
    onError: (error) => {
      setUploadStatus('error');
      onUploadError?.(error);
    },
  });

  const handlePickDocument = useCallback(async () => {
    const file = await pickDocument();
    if (file) {
      setSelectedFile(file);
      setUploadStatus('uploading');
      await upload(file);
    }
  }, [pickDocument, upload]);

  const handlePickImage = useCallback(async () => {
    const file = await pickImage();
    if (file) {
      setSelectedFile(file);
      setUploadStatus('uploading');
      await upload(file);
    }
  }, [pickImage, upload]);

  const handleDismiss = useCallback(() => {
    setSelectedFile(null);
    setUploadStatus('idle');
  }, []);

  const isProcessing = isPicking || isUploading;

  return (
    <View className="gap-4">
      {/* Upload Area */}
      <Animated.View entering={FadeInDown.duration(300)}>
        <AnimatedPressable
          onPress={handlePickDocument}
          disabled={isProcessing}
          className="rounded-2xl border-2 border-dashed p-6 items-center justify-center"
          style={{
            borderColor: isProcessing ? colors.border.light : colors.primary[400],
            backgroundColor: isProcessing ? colors.surface.secondary : `${colors.primary[600]}08`,
            opacity: isProcessing ? 0.7 : 1,
          }}
        >
          <View
            className="w-14 h-14 rounded-full items-center justify-center mb-4"
            style={{ backgroundColor: `${colors.primary[600]}15` }}
          >
            <Upload size={28} color={colors.primary[600]} />
          </View>

          <Text style={{ color: colors.text.primary }} className="text-lg font-semibold text-center">
            {title}
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-sm text-center mt-1">
            {subtitle}
          </Text>

          {Platform.OS === 'web' && (
            <Text style={{ color: colors.text.tertiary }} className="text-xs text-center mt-2">
              or drag and drop files here
            </Text>
          )}
        </AnimatedPressable>
      </Animated.View>

      {/* Quick Action Buttons */}
      <View className="flex-row gap-3">
        <AnimatedPressable
          onPress={handlePickDocument}
          disabled={isProcessing}
          className="flex-1 flex-row items-center justify-center gap-2 py-3 rounded-xl"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
            opacity: isProcessing ? 0.5 : 1,
          }}
        >
          <FileText size={18} color={colors.primary[600]} />
          <Text style={{ color: colors.primary[600] }} className="font-medium">
            Document
          </Text>
        </AnimatedPressable>

        {allowImages && (
          <AnimatedPressable
            onPress={handlePickImage}
            disabled={isProcessing}
            className="flex-1 flex-row items-center justify-center gap-2 py-3 rounded-xl overflow-hidden"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              opacity: isProcessing ? 0.5 : 1,
            }}
          >
            <Image size={18} color={colors.secondary[500]} />
            <Text style={{ color: colors.secondary[500] }} className="font-medium">
              Image
            </Text>
          </AnimatedPressable>
        )}
      </View>

      {/* Progress Indicator */}
      {selectedFile && uploadStatus !== 'idle' && (
        <UploadProgress
          fileName={selectedFile.name}
          progress={progress}
          status={uploadStatus}
          error={uploadError || pickError || undefined}
          onDismiss={handleDismiss}
        />
      )}

      {/* Error Display */}
      {(pickError || uploadError) && uploadStatus === 'idle' && (
        <View
          className="rounded-xl p-3"
          style={{ backgroundColor: `${colors.error}15` }}
        >
          <Text style={{ color: colors.error }} className="text-sm">
            {pickError || uploadError}
          </Text>
        </View>
      )}
    </View>
  );
}
